/**
 * CSRF Token Testing Utilities
 * 
 * Use these functions in your browser console to test CSRF token functionality
 */

import { CSRF_UTILS } from './constants';
import authApi from '../api/authApi';

export const csrfTest = {
  // Test 1: Check if CSRF token exists
  checkToken: () => {
    console.log('🔍 Testing CSRF Token Existence...');
    CSRF_UTILS.debugCsrfToken();
    
    const hasToken = CSRF_UTILS.hasCsrfToken();
    console.log(hasToken ? '✅ CSRF token found' : '❌ No CSRF token found');
    
    return hasToken;
  },

  // Test 2: Test refresh token with CSRF
  testRefresh: async () => {
    console.log('🔄 Testing Token Refresh with CSRF...');
    
    try {
      const response = await authApi.refreshToken();
      console.log('✅ Refresh successful:', response);
      return true;
    } catch (error: any) {
      console.error('❌ Refresh failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      return false;
    }
  },

  // Test 3: Test logout with CSRF
  testLogout: async () => {
    console.log('🚪 Testing Logout with CSRF...');
    
    try {
      const response = await authApi.logout();
      console.log('✅ Logout successful:', response);
      return true;
    } catch (error: any) {
      console.error('❌ Logout failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      return false;
    }
  },

  // Test 4: Monitor network requests
  monitorRequests: () => {
    console.log('👀 Starting request monitoring...');
    
    // Override fetch to monitor requests
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [url, options] = args;
      
      if (typeof url === 'string' && url.includes('/auth/')) {
        console.log('📡 Auth Request:', {
          url,
          method: options?.method || 'GET',
          headers: options?.headers,
          credentials: options?.credentials
        });
      }
      
      return originalFetch.apply(this, args);
    };

    console.log('✅ Request monitoring enabled. Make some auth requests to see the logs.');
    
    // Return function to stop monitoring
    return () => {
      window.fetch = originalFetch;
      console.log('🛑 Request monitoring stopped.');
    };
  },

  // Test 5: Full authentication flow test
  fullTest: async () => {
    console.log('🧪 Running Full CSRF Test Suite...');
    
    const results = {
      tokenExists: false,
      refreshWorks: false,
      logoutWorks: false
    };

    // Test 1: Check token
    results.tokenExists = csrfTest.checkToken();
    
    if (!results.tokenExists) {
      console.log('⚠️ No CSRF token found. Please login first.');
      return results;
    }

    // Test 2: Test refresh
    results.refreshWorks = await csrfTest.testRefresh();
    
    // Test 3: Test logout (only if refresh worked)
    if (results.refreshWorks) {
      results.logoutWorks = await csrfTest.testLogout();
    }

    // Summary
    console.log('📊 Test Results:', results);
    
    const allPassed = Object.values(results).every(Boolean);
    console.log(allPassed ? '🎉 All tests passed!' : '⚠️ Some tests failed');
    
    return results;
  }
};

// Make it available globally for console testing
declare global {
  interface Window {
    csrfTest: typeof csrfTest;
  }
}

if (typeof window !== 'undefined') {
  window.csrfTest = csrfTest;
}

export default csrfTest;
